import * as PIXI from "pixi.js";
import { Coord } from "../../../types/Interface";
import genCoordFromAngle from "../../../utils/coord/genCoordFromAngle";
import radianToDegree from "../../../utils/math/radianToDegree";


const PINK_ANGLE_COLOR = 0xFC99C4;
const RADIUS = 400;
const OFFSET_RADIAN = Math.PI / 180 * 2.5;
const STROKE_COLOR = 0x444444;
const STROKE_WIDTH = 1.5;

const GRAD_CONFIG = {
  lineWidth: 44,
  subLineWidth: 28,
  dashLineWidth: 10,
  semiCircle: {
    big: 320,
    sub: 275,
    small: 40
  }
}


export default class ProtractorRendererFactory {
  static drawBackground(background: PIXI.Graphics) {
    const startRad = Math.PI - OFFSET_RADIAN;
    const endRad = OFFSET_RADIAN;
    const startCoord: Coord = genCoordFromAngle(startRad, RADIUS);

    background
      .moveTo(startCoord.x, startCoord.y)
      .arc(0, 0, RADIUS, startRad, endRad)
      .lineTo(startCoord.x, startCoord.y)
      .fill({ color: PINK_ANGLE_COLOR, alpha: .6 });
  }

  static drawGraduations(graduations: PIXI.Graphics) {
    drawGraduationLines(graduations);
    drawSemiCircles(graduations);
    drawInnerLines(graduations);
  }

  static drawAngleLabels(container: PIXI.Container): PIXI.Text[] {
    const angleLabels: PIXI.Text[] = [];
    const unitRad = Math.PI / 18;
    const outerRadius = GRAD_CONFIG.semiCircle.big + 20;
    const innerRadius = GRAD_CONFIG.semiCircle.big - 20;

    Array(18 + 1).fill(null).forEach((_, i) => {
      const currentRad = unitRad * i;
      const angleValue1 = radianToDegree(currentRad);
      const angleValue2 = radianToDegree(Math.PI - currentRad);

      if (angleValue1 === 90) {
        const angle90Label = container.addChild(createAngleLabel(currentRad, GRAD_CONFIG.semiCircle.big, angleValue1));
        angle90Label.style.fontSize = 28;
        angleLabels.push(angle90Label);
      } else {
        const outerAngleLabel = container.addChild(createAngleLabel(currentRad, outerRadius, angleValue1));
        const innerAngleLabel = container.addChild(createAngleLabel(currentRad, innerRadius, angleValue2));
        angleLabels.push(outerAngleLabel, innerAngleLabel);
      }
    });

    return angleLabels;
  }

  static drawMarkAngleButton(markAngleButton: PIXI.Graphics, rotation: number, color: string | number = "yellow") {
    const buttonLength = 32;
    const lineLength = 12;
    const startRad = rotation - OFFSET_RADIAN;
    const endRad = rotation + OFFSET_RADIAN;
    const startCoord = genCoordFromAngle(startRad, RADIUS);
    const endCoord = genCoordFromAngle(endRad, RADIUS);
    const startOuterCoord = genCoordFromAngle(startRad, RADIUS + buttonLength);
    const lineStartCoord = genCoordFromAngle(rotation, RADIUS);
    const lineEndCoord = genCoordFromAngle(rotation, RADIUS + lineLength);

    markAngleButton
      .clear()
      .moveTo(startCoord.x, startCoord.y)
      .lineTo(startOuterCoord.x, startOuterCoord.y)
      .arc(0, 0, RADIUS + buttonLength, startRad, endRad)
      .lineTo(endCoord.x, endCoord.y)
      .arc(0, 0, RADIUS, startRad, endRad)
      .fill({ color, alpha: .5 })
      .moveTo(lineStartCoord.x, lineStartCoord.y)
      .lineTo(lineEndCoord.x, lineEndCoord.y)
      .stroke({ width: 2, color: 0x000000 });
  }

  static drawRotateButtons(rotateLeftButton: PIXI.Graphics, rotateRightButton: PIXI.Graphics) {
    const startLeftRad = -(Math.PI + OFFSET_RADIAN);
    const endLeftRad = startLeftRad + Math.PI / 12;
    const startLeftCoord = genCoordFromAngle(startLeftRad, RADIUS);
    const endLeftCoord = genCoordFromAngle(endLeftRad, RADIUS);

    const startRightRad = Math.PI / 180 + OFFSET_RADIAN;
    const endRightRad = startRightRad - Math.PI / 12;
    const startRightCoord = genCoordFromAngle(startRightRad, RADIUS);
    const endRightCoord = genCoordFromAngle(endRightRad, RADIUS);

    drawRotateButton(rotateLeftButton, startLeftCoord, endLeftCoord, startLeftRad, endLeftRad, true);
    drawRotateButton(rotateRightButton, startRightCoord, endRightCoord, startRightRad, endRightRad, false);
  }
}

function drawRotateButton(
  rotateButton: PIXI.Graphics,
  startCoord: Coord,
  endCoord: Coord,
  startRad: number,
  endRad: number,
  isLeft: boolean
) {
  const centerX = (isLeft ? -1 : 1) * RADIUS / 3;

  rotateButton
    .moveTo(centerX, startCoord.y)
    .lineTo(startCoord.x, startCoord.y)
    .arc(0, 0, RADIUS, startRad, endRad, !isLeft)
    .lineTo(centerX, endCoord.y)
    .lineTo(centerX, startCoord.y)
    .fill({ color: 0x000000, alpha: 0 });
}

function drawGraduationLines(graduations: PIXI.Graphics) {
  const unitRad = Math.PI / 36;

  Array(36 + 1).fill(null).forEach((_, i) => {
    const rad = Math.PI + unitRad * i;
    const startCoord = genCoordFromAngle(rad, RADIUS);
    const endCoord = genCoordFromAngle(rad, RADIUS - (i === 18 ? GRAD_CONFIG.lineWidth + 12 : GRAD_CONFIG.lineWidth));

    graduations
      .moveTo(startCoord.x, startCoord.y)
      .lineTo(endCoord.x, endCoord.y)
      .stroke({ width: STROKE_WIDTH, color: STROKE_COLOR });

    if (i === 36) return;
    drawSubGraduationLines(graduations, rad);
  });
}

function drawSubGraduationLines(graduations: PIXI.Graphics, baseRad: number) {
  const subUnitRad = Math.PI / 180;

  Array(5).fill(null).forEach((_, i) => {
    if (i === 4) return;
    const subRad = baseRad + subUnitRad * (i + 1);
    const startCoord = genCoordFromAngle(subRad, RADIUS);
    const endCoord = genCoordFromAngle(subRad, RADIUS - GRAD_CONFIG.subLineWidth);

    graduations
      .moveTo(startCoord.x, startCoord.y)
      .lineTo(endCoord.x, endCoord.y)
      .stroke({ width: STROKE_WIDTH, color: STROKE_COLOR });
  });
}

function drawSemiCircles(graduations: PIXI.Graphics) {
  const bigCircleStart = genCoordFromAngle(Math.PI, GRAD_CONFIG.semiCircle.big);
  const bigCircleSecondStart = genCoordFromAngle(-Math.PI / 180 * 85, GRAD_CONFIG.semiCircle.big);
  const subCircleStart = genCoordFromAngle(Math.PI, GRAD_CONFIG.semiCircle.sub);
  const smallCircleStart = genCoordFromAngle(Math.PI, GRAD_CONFIG.semiCircle.small);

  graduations
    .moveTo(bigCircleStart.x, bigCircleStart.y)
    .arc(0, 0, GRAD_CONFIG.semiCircle.big, Math.PI, -Math.PI / 180 * 95)

    .moveTo(bigCircleSecondStart.x, bigCircleSecondStart.y)
    .arc(0, 0, GRAD_CONFIG.semiCircle.big, -Math.PI / 180 * 85, 0)

    .moveTo(subCircleStart.x, subCircleStart.y)
    .arc(0, 0, GRAD_CONFIG.semiCircle.sub, Math.PI, 0)

    .moveTo(smallCircleStart.x, smallCircleStart.y)
    .arc(0, 0, GRAD_CONFIG.semiCircle.small, Math.PI, 0)

    .stroke({ width: STROKE_WIDTH, color: STROKE_COLOR });
}

function drawInnerLines(graduations: PIXI.Graphics) {
  const unitRad = Math.PI / 18;

  Array(18 + 1).fill(null).forEach((_, i) => {
    const rad = Math.PI + unitRad * i;
    const innerEndRadius = [0, 9, 18].includes(i) ? 0 : GRAD_CONFIG.semiCircle.small;
    const innerStartRadius = GRAD_CONFIG.semiCircle.sub + (i !== 9 ? GRAD_CONFIG.dashLineWidth / 2 : GRAD_CONFIG.dashLineWidth);

    const dashStartCoord = genCoordFromAngle(rad, GRAD_CONFIG.semiCircle.big + GRAD_CONFIG.dashLineWidth / 2);
    const dashEndCoord = genCoordFromAngle(rad, GRAD_CONFIG.semiCircle.big - GRAD_CONFIG.dashLineWidth / 2);
    const innerStartCoord = genCoordFromAngle(rad, innerStartRadius);
    const innerEndCoord = genCoordFromAngle(rad, innerEndRadius);

    graduations
      .moveTo(innerStartCoord.x, innerStartCoord.y)
      .lineTo(innerEndCoord.x, innerEndCoord.y)
      .stroke({ width: STROKE_WIDTH, color: STROKE_COLOR });

    if (i === 9) return;

    graduations
      .moveTo(dashStartCoord.x, dashStartCoord.y)
      .lineTo(dashEndCoord.x, dashEndCoord.y)
      .stroke({ width: STROKE_WIDTH, color: STROKE_COLOR });
  });
}

function createAngleLabel(baseRad: number, radius: number, angleValue: number) {
  const labelRad = Math.PI + baseRad;
  const labelCoord = genCoordFromAngle(labelRad, radius);
  const label = new PIXI.Text();

  label.text = angleValue;
  label.style.fontSize = 19;
  label.anchor.set(.5);
  label.rotation = baseRad - Math.PI / 2;
  Object.assign(label, labelCoord);

  return label;
}